import React, { useEffect, useState, useCallback } from "react";
import API from "../../../services/api";

const statusColors = {
  Planned: "bg-secondary-subtle text-secondary",
  Sanctioned: "bg-info-subtle text-info",
  Tendered: "bg-primary-subtle text-primary",
  "Work Started": "bg-warning-subtle text-warning",
  "In Progress": "bg-orange-subtle text-warning",
  "On Hold": "bg-danger-subtle text-danger",
  Completed: "bg-success-subtle text-success",
  Cancelled: "bg-dark-subtle text-dark",
};

const DevProjects = () => {
  const [projects, setProjects] = useState([]);
  const [villages, setVillages] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [prjCats, setPrjCats] = useState([]);
  const [hoas, setHoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const emptyForm = {
    name: "",
    budget_type: "Regular",
    villageid: "",
    deptid: "",
    financial_year: "",
    status: "Planned",
    headaccountid: "",
    prjcatid: "",
    tender_amount: "",
    estimated_amount: "",
    target_completion_date: "",
    contractor_name: "",
    contractor_mobile: "",
  };
  const [form, setForm] = useState(emptyForm);

  // ── Fetch Masters ─────────────────────────────────────────
  const fetchMasters = useCallback(async () => {
    try {
      const [vills, depts, cats, hoa] = await Promise.all([
        API.get("/masters/villages"),
        API.get("/masters/departments"),
        API.get("/masters/projectcategories"),
        API.get("/masters/headofaccounts"),
      ]);
      setVillages(vills.data.data);
      setDepartments(depts.data.data);
      setPrjCats(cats.data.data);
      setHoas(hoa.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Projects ────────────────────────────────────────
  const fetchProjects = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/development-projects/", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            status: filterStatus || undefined,
            financial_year: filterYear || undefined,
          },
        });
        setProjects(res.data.data.projects);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, filterStatus, filterYear],
  );

  useEffect(() => {
    fetchMasters();
    fetchProjects();
  }, []);

  // ── Open Add ──────────────────────────────────────────────
  const openAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────
  const openEdit = (p) => {
    setEditItem(p);
    setForm({
      name: p.name || "",
      budget_type: p.budget_type || "Regular",
      villageid: p.villageid || "",
      deptid: p.deptid || "",
      financial_year: p.financial_year || "",
      status: p.status || "Planned",
      headaccountid: p.headaccountid || "",
      prjcatid: p.prjcatid || "",
      tender_amount: p.tender_amount || "",
      estimated_amount: p.estimated_amount || "",
      target_completion_date: p.target_completion_date || "",
      contractor_name: p.contractor_name || "",
      contractor_mobile: p.contractor_mobile || "",
    });
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        villageid: form.villageid ? parseInt(form.villageid) : null,
        deptid: form.deptid ? parseInt(form.deptid) : null,
        headaccountid: form.headaccountid ? parseInt(form.headaccountid) : null,
        prjcatid: form.prjcatid ? parseInt(form.prjcatid) : null,
        tender_amount: form.tender_amount
          ? parseFloat(form.tender_amount)
          : null,
        estimated_amount: form.estimated_amount
          ? parseFloat(form.estimated_amount)
          : null,
      };
      if (editItem) {
        await API.put(`/development-projects/${editItem.project_id}`, payload);
      } else {
        await API.post("/development-projects/", payload);
      }
      setShowModal(false);
      fetchProjects(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving project");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    setDeleting(id);
    try {
      await API.delete(`/development-projects/${id}`);
      fetchProjects(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setDeleting(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProjects(1);
  };

  // Financial years list
  const years = [];
  for (let y = 2020; y <= 2030; y++) {
    years.push(`${y}-${y + 1}`);
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Development Projects</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> projects
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={openAdd}
        >
          <i className="bi bi-plus-lg me-2"></i>Add Project
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <form onSubmit={handleSearch}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by project name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  {[
                    "Planned",
                    "Sanctioned",
                    "Tendered",
                    "Work Started",
                    "In Progress",
                    "On Hold",
                    "Completed",
                    "Cancelled",
                  ].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                >
                  <option value="">All Years</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <button type="submit" className="btn btn-primary w-100">
                  <i className="bi bi-search me-1"></i>Search
                </button>
              </div>
              <div className="col-6 col-md-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    setSearch("");
                    setFilterStatus("");
                    setFilterYear("");
                    fetchProjects(1);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-building fs-1 d-block mb-2"></i>
              No development projects found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Project Name
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Budget Type
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Financial Year
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Est. Amount
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Status
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Contractor
                    </th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, idx) => (
                    <tr key={p.project_id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td style={{ maxWidth: "220px" }}>
                        <div className="fw-semibold small text-truncate">
                          {p.name}
                        </div>
                        {p.financial_year && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            FY: {p.financial_year}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge small ${
                            p.budget_type === "Regular"
                              ? "bg-info-subtle text-info"
                              : "bg-warning-subtle text-warning"
                          }`}
                        >
                          {p.budget_type}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {p.financial_year || "—"}
                      </td>
                      <td className="small">
                        {p.estimated_amount ? (
                          <span className="fw-medium">
                            ₹
                            {Number(p.estimated_amount).toLocaleString("en-IN")}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge small ${statusColors[p.status] || "bg-secondary-subtle"}`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td>
                        <div className="small">{p.contractor_name || "—"}</div>
                        {p.contractor_mobile && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {p.contractor_mobile}
                          </div>
                        )}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-info me-1"
                          onClick={() => {
                            setDetailItem(p);
                            setShowDetail(true);
                          }}
                          title="View"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => openEdit(p)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(p.project_id)}
                          disabled={deleting === p.project_id}
                        >
                          {deleting === p.project_id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchProjects(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchProjects(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchProjects(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  {editItem ? "Edit Project" : "Add Development Project"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSave}>
                <div
                  className="modal-body px-4 py-3"
                  style={{ maxHeight: "65vh", overflowY: "auto" }}
                >
                  <div className="row g-3">
                    {/* Name */}
                    <div className="col-12">
                      <label className="form-label small fw-medium">
                        Project Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter project name"
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    {/* Budget Type + Status */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Budget Type
                      </label>
                      <select
                        className="form-select"
                        value={form.budget_type}
                        onChange={(e) =>
                          setForm({ ...form, budget_type: e.target.value })
                        }
                      >
                        <option value="Regular">Regular</option>
                        <option value="Additional">Additional</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Status
                      </label>
                      <select
                        className="form-select"
                        value={form.status}
                        onChange={(e) =>
                          setForm({ ...form, status: e.target.value })
                        }
                      >
                        {[
                          "Planned",
                          "Sanctioned",
                          "Tendered",
                          "Work Started",
                          "In Progress",
                          "On Hold",
                          "Completed",
                          "Cancelled",
                        ].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Village + Department */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Village
                      </label>
                      <select
                        className="form-select"
                        value={form.villageid}
                        onChange={(e) =>
                          setForm({ ...form, villageid: e.target.value })
                        }
                      >
                        <option value="">Select Village</option>
                        {villages.map((v) => (
                          <option key={v.villageid} value={v.villageid}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Department
                      </label>
                      <select
                        className="form-select"
                        value={form.deptid}
                        onChange={(e) =>
                          setForm({ ...form, deptid: e.target.value })
                        }
                      >
                        <option value="">Select Department</option>
                        {departments.map((d) => (
                          <option key={d.deptid} value={d.deptid}>
                            {d.deptname}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Financial Year + Project Category */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Financial Year
                      </label>
                      <select
                        className="form-select"
                        value={form.financial_year}
                        onChange={(e) =>
                          setForm({ ...form, financial_year: e.target.value })
                        }
                      >
                        <option value="">Select Year</option>
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Project Category
                      </label>
                      <select
                        className="form-select"
                        value={form.prjcatid}
                        onChange={(e) =>
                          setForm({ ...form, prjcatid: e.target.value })
                        }
                      >
                        <option value="">Select Category</option>
                        {prjCats.map((c) => (
                          <option key={c.prjcatid} value={c.prjcatid}>
                            {c.prjcatname}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Head of Account */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Head of Account
                      </label>
                      <select
                        className="form-select"
                        value={form.headaccountid}
                        onChange={(e) =>
                          setForm({ ...form, headaccountid: e.target.value })
                        }
                      >
                        <option value="">Select HOA</option>
                        {hoas.map((h) => (
                          <option key={h.headaccountid} value={h.headaccountid}>
                            {h.headaccount}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Tender Amount + Estimated Amount */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Tender Amount (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0.00"
                        step="0.01"
                        value={form.tender_amount}
                        onChange={(e) =>
                          setForm({ ...form, tender_amount: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Estimated Amount (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0.00"
                        step="0.01"
                        value={form.estimated_amount}
                        onChange={(e) =>
                          setForm({ ...form, estimated_amount: e.target.value })
                        }
                      />
                    </div>

                    {/* Target Completion Date */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Target Completion Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.target_completion_date}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            target_completion_date: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Contractor Name + Mobile */}
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Contractor Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Contractor name"
                        value={form.contractor_name}
                        onChange={(e) =>
                          setForm({ ...form, contractor_name: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Contractor Mobile
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Mobile number"
                        value={form.contractor_mobile}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            contractor_mobile: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn text-white px-4"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {editItem ? "Update" : "Save"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && detailItem && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="modal-title fw-bold mb-1">
                    {detailItem.name}
                  </h5>
                  <span
                    className={`badge small ${statusColors[detailItem.status]}`}
                  >
                    {detailItem.status}
                  </span>
                </div>
                <button
                  className="btn-close"
                  onClick={() => setShowDetail(false)}
                ></button>
              </div>
              <div className="modal-body px-4 pb-4">
                <div className="row g-3">
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Budget Type</div>
                    <div className="fw-medium small">
                      {detailItem.budget_type}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Financial Year</div>
                    <div className="fw-medium small">
                      {detailItem.financial_year || "—"}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Estimated Amount</div>
                    <div className="fw-medium small">
                      {detailItem.estimated_amount
                        ? `₹${Number(detailItem.estimated_amount).toLocaleString("en-IN")}`
                        : "—"}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted small">Tender Amount</div>
                    <div className="fw-medium small">
                      {detailItem.tender_amount
                        ? `₹${Number(detailItem.tender_amount).toLocaleString("en-IN")}`
                        : "—"}
                    </div>
                  </div>
                  <div className="col-12">
                    <hr className="my-1" />
                  </div>
                  <div className="col-6">
                    <div className="text-muted small">Contractor</div>
                    <div className="fw-medium small">
                      {detailItem.contractor_name || "—"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-muted small">Contractor Mobile</div>
                    <div className="fw-medium small">
                      {detailItem.contractor_mobile || "—"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-muted small">Target Date</div>
                    <div className="fw-medium small">
                      {detailItem.target_completion_date || "—"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-muted small">Completed Date</div>
                    <div className="fw-medium small">
                      {detailItem.completed_date || "—"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-muted small">Created By</div>
                    <div className="fw-medium small">
                      {detailItem.created_by || "—"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-muted small">Created At</div>
                    <div className="fw-medium small">
                      {detailItem.created_at || "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevProjects;
